import type { JSX } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button as AntButton, Skeleton } from 'antd';

import { ApiError, friendlyError } from '../../lib/api';
import { OPENING_TASKS_KEYS, openingTasksApi, type OpeningPlanListStatus } from '../../lib/api/openingTasks';
import { PageTitle } from '../ds';
import { retryOpeningQuery } from '../opening-tasks/opening-query';
import './OpeningPlan.css';
import './OpeningManager.css';
import { OpeningManagerList } from './OpeningManagerList';
import { OpeningPlanView } from './OpeningPlanView';
import { cardIdFromParams, listHref, statusFromParams } from './opening-manager-model';
import { PLAN_COPY } from './plan-model';

/** «← Все открытия»: назад к списку с тем же фильтром (`?status=` из адреса карточки). */
function BackToList({ status }: { status: OpeningPlanListStatus }): JSX.Element {
  return (
    <Link to={listHref(status)} className="fk-opm-back">
      <span aria-hidden="true">←</span> Все открытия
    </Link>
  );
}

/**
 * План одной карточки для менеджера УК (`GET /wiki/opening/plans/:id`): тот же визард, что у партнёра, но только для
 * чтения. 404 — карточки нет или она вне области менеджера: подпись и возврат к списку.
 */
function OpeningManagerPlan({ id, status }: { id: string; status: OpeningPlanListStatus }): JSX.Element {
  const plan = useQuery({
    queryKey: OPENING_TASKS_KEYS.planById(id),
    queryFn: () => openingTasksApi.planById(id),
    retry: retryOpeningQuery,
    staleTime: 30_000,
  });
  const back = <BackToList status={status} />;

  if (plan.data?.card) return <OpeningPlanView card={plan.data.card} mode="manager" crumbs={back} />;

  const notFound = (plan.error instanceof ApiError && plan.error.status === 404) || (!plan.error && !plan.isLoading && !plan.data?.card);
  return (
    <div className="fk-op">
      <PageTitle title="Открытие точки" sub={PLAN_COPY.manager.sub} crumbs={back} style={{ marginBottom: 0 }} />
      {plan.isLoading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : notFound ? (
        <Alert type="warning" showIcon message="Карточка не найдена или не в вашей области" />
      ) : (
        <Alert
          type="error"
          showIcon
          message="Не удалось загрузить план открытия"
          description={friendlyError(plan.error)}
          action={
            <AntButton size="small" onClick={() => void plan.refetch()} loading={plan.isFetching}>
              Повторить
            </AntButton>
          }
        />
      )}
    </div>
  );
}

/**
 * «Открытие точки» менеджера УК (владелец 31.10.2026): без `?card=` — список открытий партнёров, с ним — план этой
 * карточки (только чтение). Фильтр списка (`?status=`) в адресе сохраняется и в карточке.
 */
export function OpeningManagerScreen(): JSX.Element {
  const [sp] = useSearchParams();
  const status = statusFromParams(sp);
  const cardId = cardIdFromParams(sp);
  return cardId ? <OpeningManagerPlan key={cardId} id={cardId} status={status} /> : <OpeningManagerList status={status} />;
}
